import { useMemo } from 'react';
import { PageType } from '@vgtv/api-client/lib/page';

import {
  getCategoryBackgroundImage,
  getCategoryLogoImage,
} from '../../shared/helpers/categoryHelpers';
import {
  getTagBackgroundImage,
  getTagMainImage,
} from '../../shared/helpers/tagHelpers';
import { Image } from '../Image/Image';
import { getImageDefaultSrc, getImageSources } from '../../helpers/images';

import type { CnpTag, TagEnrichment } from '@vgtv/api-client/lib/cnp_tag';
import type {
  CategoryPage,
  StoryPage,
  TagPage,
} from '@vgtv/api-client/lib/page';
import type { FC } from 'react';

import * as styles from './PageHeaderSplash.module.scss';

interface Props {
  pageConfiguration: CategoryPage | TagPage | StoryPage;
}

export const PageHeaderSplash: FC<Props> = ({ pageConfiguration }) => {
  const pageType = pageConfiguration.type;

  let title: string;
  let description: string | undefined;
  let imageUrl: string | undefined;
  let iconImageUrl: string | undefined;

  switch (pageType) {
    case PageType.TAG: {
      const entity = pageConfiguration[pageType] as CnpTag &
        Pick<TagEnrichment, 'images'>;
      title = entity.title;
      imageUrl = getTagBackgroundImage(entity);
      iconImageUrl = getTagMainImage(entity);
      break;
    }
    case PageType.CATEGORY: {
      const entity = pageConfiguration[pageType];
      title = entity.title;
      description = entity?.additional?.description ?? undefined;
      imageUrl = getCategoryBackgroundImage(entity);
      iconImageUrl = getCategoryLogoImage(entity);
      break;
    }
    case PageType.STORY: {
      const entity = pageConfiguration[pageType];
      title = entity.title;
      break;
    }
  }

  imageUrl = imageUrl || iconImageUrl;

  const backgroundStyle = useMemo(() => {
    return imageUrl
      ? {
          backgroundImage: `url("${getImageDefaultSrc(imageUrl, 'splashBackground')}")`,
        }
      : undefined;
  }, [imageUrl]);

  const iconImageSources = useMemo(
    () =>
      getImageSources({
        imageUrl: iconImageUrl,
        imagePreset: 'splashIcon',
        aspectRatio: 'square',
      }),
    [iconImageUrl]
  );

  return (
    <div className={styles.container}>
      {imageUrl && (
        <div className={styles.background} style={backgroundStyle} />
      )}
      {iconImageUrl && (
        <Image
          className={styles.icon}
          imageSources={iconImageSources}
          isRound
        />
      )}
      <div className={styles.content}>
        <h1 className={styles.title}>{title}</h1>
        <div className={styles.description}>{description}</div>
      </div>
    </div>
  );
};
